'use client';

import { MouseEvent, ReactNode } from 'react';

import Box from '@mui/material/Box';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { PopoverOrigin } from '@mui/material/Popover';
import { SxProps, Theme } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';

import { Link } from './link';

interface LinkAction {
  disabled?: boolean;
  href: HTMLAnchorElement['href'];
  icon?: typeof SvgIcon;
  id: string;
  isDangerous?: boolean;
  label: ReactNode;
  startIcon?: typeof SvgIcon;
  target?: HTMLAnchorElement['target'];
}

interface ButtonAction {
  disabled?: boolean;
  href?: never;
  icon?: typeof SvgIcon;
  id: string;
  isDangerous?: boolean;
  label: ReactNode;
  onClick: () => void;
  startIcon?: typeof SvgIcon;
}

interface Props {
  actions: (LinkAction | ButtonAction)[];
  anchorEl: HTMLButtonElement | null;
  anchorOrigin?: PopoverOrigin;
  iconPosition?: 'start' | 'end';
  id: string;
  onClose: () => void;
  open: boolean;
  sx?: SxProps<Theme>;
  toggleId: string;
  transformOrigin?: PopoverOrigin;
}

function isLinkAction(action: LinkAction | ButtonAction): action is LinkAction {
  return 'href' in action;
}

export type MenuActionsProps = Props;

export function MenuActions({
  actions,
  anchorEl,
  anchorOrigin,
  iconPosition = 'end',
  id,
  onClose,
  open,
  sx,
  toggleId,
  transformOrigin,
}: Props) {
  const generateActionHandler = (action: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    onClose();
    action();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={
          anchorOrigin || {
            horizontal: 'right',
            vertical: 'top',
          }
        }
        id={id}
        MenuListProps={{
          'aria-labelledby': toggleId,
          sx,
        }}
        onClick={(event) => event.stopPropagation()}
        onClose={onClose}
        open={open}
        transformOrigin={
          transformOrigin || {
            horizontal: 'right',
            vertical: -40,
          }
        }
      >
        {actions.map((action) =>
          isLinkAction(action) ? (
            <MenuItem
              key={action.id}
              component={Link}
              href={action.href}
              onClick={onClose}
              {...(action.target ? { target: action.target } : {})}
              disabled={action.disabled}
              sx={({ palette }) => ({
                color: action.isDangerous ? palette.error.main : undefined,
                display: 'flex',
                flexDirection: iconPosition === 'end' ? 'row' : 'row-reverse',
                gap: 1,
                justifyContent: iconPosition === 'end' ? 'flex-start' : 'flex-end',
              })}
            >
              {action.startIcon && (
                <action.startIcon
                  sx={({ palette }) => ({
                    fill: action.isDangerous ? palette.error.main : palette.text.secondary,
                  })}
                />
              )}
              <Box
                component="span"
                sx={({ palette }) => ({
                  color: action.isDangerous ? palette.error.main : undefined,
                })}
              >
                {action.label}
              </Box>
              {action.icon && (
                <action.icon
                  sx={({ palette }) => ({
                    fill: action.isDangerous ? palette.error.main : palette.text.secondary,
                  })}
                />
              )}
            </MenuItem>
          ) : (
            <MenuItem
              key={action.id}
              disabled={action.disabled}
              onClick={generateActionHandler(action.onClick)}
              sx={({ palette }) => ({
                color: action.isDangerous ? palette.error.main : undefined,
                display: 'flex',
                flexDirection: iconPosition === 'end' ? 'row' : 'row-reverse',
                gap: 1,
                justifyContent: iconPosition === 'end' ? 'flex-start' : 'flex-end',
              })}
            >
              {action.startIcon && (
                <action.startIcon
                  sx={({ palette }) => ({
                    fill: action.isDangerous ? palette.error.main : palette.text.secondary,
                  })}
                />
              )}
              <Box
                component="span"
                sx={({ palette }) => ({
                  color: action.isDangerous ? palette.error.main : undefined,
                })}
              >
                {action.label}
              </Box>
              {action.icon && (
                <action.icon
                  sx={({ palette }) => ({
                    fill: action.isDangerous ? palette.error.main : palette.text.secondary,
                  })}
                />
              )}
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  );
}
